"use client";

import type { Voice } from "@/app/_data/voices";

import { ttsLanguages as languages, ttsLanguagesMap } from "@components/languages/config";
import { defaultV3Prompt } from "@components/languages/promptData/en";
import { TTSModels } from "@mw/shared/models";
import { type PlayingState, useTtsGeneration } from "@ui/hooks/useTtsGeneration";
import { cn } from "@ui/lib/utils";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clsx } from "clsx";

import { LimitReachedOverlay } from "../../LimitReachedOverlay";
import { LanguageSelect, VoiceSelector } from "./TTSControls";
import { TextAreaWithHighlighter } from "./TTSTextInput";
import { getVoices } from "../fetchVoices";
import { runAfterNextBrowserPaint } from "@utilities/afterNextPaint";
import { easedGradientStops } from "@ui/lib/gradients";

import { ArrowUp } from "lucide-react";

function sortLanguagesWithEnglishFirst(a: (typeof languages)[0], b: (typeof languages)[0]) {
  if (a.value === "en") return -1;
  if (b.value === "en") return 1;
  return a.label.localeCompare(b.label);
}

interface ProductDemoProps {
  className?: string;
  defaultLanguage?: string;
  defaultText?: string;
  voices?: Voice[];
  useChevronButton?: boolean;
}

export const ProductDemo = ({
  className,
  defaultLanguage,
  defaultText,
  voices: initialVoices,
  useChevronButton,
}: ProductDemoProps) => {
  const locale = useLocale();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // State
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage ?? locale);
  const [text, setText] = useState(defaultText ?? defaultV3Prompt.text);
  const [voices, setVoices] = useState<Voice[]>(initialVoices ?? []);
  const [selectedVoice, setSelectedVoice] = useState<Voice | undefined>(undefined);
  const [voicesLoading, setVoicesLoading] = useState(false);

  // Use the TTS generation hook
  const { generate, playingState, setPlayingState, alignment, regenerate, setRegenerate, showLimitReachedOverlay } =
    useTtsGeneration(text, selectedVoice, 1, true, "tts_omnibox");

  // Derived state
  const language = ttsLanguagesMap[selectedLanguage];

  const sortedLanguages = useMemo(() => languages.sort(sortLanguagesWithEnglishFirst), []);

  // Update selected voice when voices change
  useEffect(() => {
    if (voices.length > 0 && !selectedVoice) {
      const preferredVoice = defaultV3Prompt.preferredVoices?.[0];
      if (preferredVoice && voices.find(voice => voice.voiceId === preferredVoice.voiceId)) {
        setSelectedVoice(preferredVoice);
      } else {
        setSelectedVoice(voices[0]);
      }
    }
  }, [voices, selectedVoice]);

  // Fetch voices for the current language
  const fetchVoicesForCurrentLanguage = useCallback(
    async (languageCode: string) => {
      setVoicesLoading(true);
      const fetchedVoices = await getVoices(languageCode, initialVoices);
      setVoices(fetchedVoices);

      // Update selected voice if current one is not in the new list
      if (fetchedVoices.length > 0 && !fetchedVoices.find(voice => voice.voiceId === selectedVoice?.voiceId)) {
        setSelectedVoice(fetchedVoices[0]);
      }
      setVoicesLoading(false);
    },
    [initialVoices, selectedVoice?.voiceId],
  );

  // Fetch voices on mount and language change
  useEffect(() => {
    fetchVoicesForCurrentLanguage(selectedLanguage);
  }, [selectedLanguage]);

  const handleGenerate = useCallback(async () => {
    if (!audioRef.current || !selectedVoice) return;

    await generate({
      textPrompt: text,
      audioElement: audioRef.current,
      modelId: TTSModels.ELEVEN_V3,
      voice: selectedVoice,
    });
  }, [text, selectedVoice, generate]);

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return;

    if (playingState === "playing" || playingState === "loading") {
      audioRef.current.pause();
      setPlayingState("paused");
    } else {
      audioRef.current.play();
      setPlayingState("playing");
    }
  }, [playingState, setPlayingState]);

  const handlePlay = useCallback(() => {
    if (regenerate) {
      handleGenerate();
    } else {
      handlePlayPause();
    }
  }, [regenerate, handleGenerate, handlePlayPause]);

  const handleTextChange = useCallback(
    (newText: string) => {
      runAfterNextBrowserPaint(() => {
        setText(newText);
        setRegenerate(true);
      });
    },
    [setRegenerate],
  );

  const handleVoiceChange = useCallback(
    (voice: Voice) => {
      setSelectedVoice(voice);
      setRegenerate(true);
    },
    [setRegenerate],
  );

  const handleLanguageChange = useCallback(
    (languageCode: string) => {
      setSelectedLanguage(languageCode);
      setRegenerate(true);
    },
    [setRegenerate],
  );

  const handleAudioEnded = useCallback(() => {
    setPlayingState("idle");
  }, [setPlayingState]);

  const handleAudioPlay = useCallback(() => {
    setPlayingState("playing");
  }, [setPlayingState]);

  const handleAudioPause = useCallback(() => {
    setPlayingState("paused");
  }, [setPlayingState]);

  const isPlayButtonDisabled = !text || !selectedVoice || voicesLoading;

  if (showLimitReachedOverlay) {
    return (
      <div className={cn("relative h-full overflow-hidden", className)}>
        <LimitReachedOverlay rounded />
      </div>
    );
  }

  return (
    <div className={cn("flex h-full flex-col gap-10", className)}>
      {/* Top row: Voice and Language selectors */}
      <div className='flex shrink-0 items-center gap-8'>
        <VoiceSelector
          loading={voicesLoading}
          onVoiceChange={handleVoiceChange}
          selectedVoice={selectedVoice}
          size='md'
          voices={voices}
          variant='simple'
          showSelectedAvatar={false}
          triggerVariant='outline-new'
          overlayStyle={true}
        />

        <LanguageSelect
          language={language}
          onLanguageChange={handleLanguageChange}
          showSelectedLabel
          sortedLanguages={sortedLanguages}
          triggerSize='md'
          showFlag={false}
          variant='simple'
          triggerVariant='outline-new'
          overlayStyle={true}
        />
      </div>

      {/* Text editor */}
      <div
        className='tw-bg-white tw-overflow-hidden tw-rounded-[1.25rem] tw-relative tw-flex tw-min-h-0 tw-flex-auto tw-h-full tw-flex-col'
        style={{ boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 2px 2px rgb(0 0 0 / 0.04)" }}
      >
        <div className='tw-min-h-0 tw-flex-auto tw-relative'>
          <TextAreaWithHighlighter
            alignment={alignment}
            audioElementRef={audioRef}
            isStreaming
            onTextChange={handleTextChange}
            playingState={playingState}
            text={text}
            withBrackets
            className='tw-text-sm/6 !tw-px-5 !tw-py-4 tw-text-gray-700 tw-scroll-py-4 tw-scrollbar-none'
          />
        </div>

        {/* Play button */}
        <div className='tw-relative tw-bg-white tw-px-4 tw-pb-4'>
          <div
            className='tw-absolute tw-inset-x-0 tw-bottom-full tw-h-4 tw-pointer-events-none'
            style={{ background: `linear-gradient(to top, ${easedGradientStops("#fff", "#fff0")})` }}
          />
          <PlayButton
            disabled={isPlayButtonDisabled}
            onPlay={handlePlay}
            playingState={playingState}
            useChevron={useChevronButton}
          />
        </div>
      </div>

      {/* Hidden audio element */}
      <audio
        className='hidden'
        onEnded={handleAudioEnded}
        onPause={handleAudioPause}
        onPlay={handleAudioPlay}
        ref={audioRef}
      />
    </div>
  );
};

export default ProductDemo;

function PlayButton({
  playingState,
  onPlay,
  disabled,
  useChevron,
}: {
  disabled?: boolean;
  onPlay: () => void;
  playingState: PlayingState;
  useChevron?: boolean;
}) {
  const t = useTranslations("ClientKeys.Globals");

  return (
    <button
      type='button'
      className={clsx(
        "tw-group tw-isolate tw-relative tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-ml-auto tw-size-10 tw-bg-cream tw-rounded-full tw-transition-colors",
        disabled ? "tw-cursor-not-allowed tw-text-gray-300" : "tw-text-black",
      )}
      disabled={disabled}
      onClick={onPlay}
      aria-label={playingState === "playing" ? t("pause") : t("play")}
    >
      <div
        className={clsx(
          "tw-absolute tw-inset-0 tw-bg-black/2.5 -tw-z-10 tw-opacity-0 tw-transition-opacity",
          !disabled && "group-hover:tw-opacity-100",
        )}
      />
      <svg
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinejoin='round'
        strokeLinecap='round'
        className={clsx("tw-size-5", playingState === "loading" && "tw-animate-spin")}
        style={{ animationDuration: "0.7s" }}
        aria-hidden
      >
        {playingState === "loading" ? (
          <>
            <path
              d='M21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12Z'
              strokeOpacity='0.1'
            />
            <path d='M21.25 12C21.25 17.1086 17.1086 21.25 12 21.25' />
          </>
        ) : playingState === "playing" ? (
          <>
            <path d='M4.75 5.75C4.75 4.64543 5.64543 3.75 6.75 3.75H7.25C8.35457 3.75 9.25 4.64543 9.25 5.75V18.25C9.25 19.3546 8.35457 20.25 7.25 20.25H6.75C5.64543 20.25 4.75 19.3546 4.75 18.25V5.75Z' />
            <path d='M14.75 5.75C14.75 4.64543 15.6454 3.75 16.75 3.75H17.25C18.3546 3.75 19.25 4.64543 19.25 5.75V18.25C19.25 19.3546 18.3546 20.25 17.25 20.25H16.75C15.6454 20.25 14.75 19.3546 14.75 18.25V5.75Z' />
          </>
        ) : useChevron ? (
          <ArrowUp className='tw-size-5' />
        ) : (
          <path d='M20.155 10.3209L8.8365 2.99715C7.50578 2.13609 5.75 3.09128 5.75 4.67629V19.3237C5.75 20.9087 7.50577 21.8639 8.8365 21.0029L20.155 13.6791C21.373 12.891 21.373 11.109 20.155 10.3209Z' />
        )}
      </svg>
    </button>
  );
}
